import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { Message } from '@mews-commander/messages';
import { Container, getSpacingToken, getToken, Stack } from '@optimus-web/core';
import { AssistantMessageRecordDto, AssistantMessageRole } from '@mews-commander/data-access';
import { SuggestionActionsMenu } from './SuggestionActionsMenu';
import { SuggestionSummaryMenu } from './SuggestionSummaryMenu';
import { DraftAReply } from './DraftAReply';

interface AiSuggestionsProps {
    lastMessage: Message;
    text: string;
    handleSelectedSuggestion: (suggestion: string) => void;
}

export const AiSuggestions = ({ lastMessage, text, handleSelectedSuggestion }: AiSuggestionsProps) => {
    const [showDraftAReply, setShowDraftAReply] = useState(true);
    const [selectedSummary, setSelectedSummary] = useState<string | undefined>();
    const [showSuggestionSummaryMenu, setShowSuggestionSummaryMenu] = useState(false);
    const [showSuggestionActionsMenu, setShowSuggestionActionsMenu] = useState(false);
    const messageHistory: AssistantMessageRecordDto[] = [
        {
            content: lastMessage.text,
            role: AssistantMessageRole.Assistant,
        },
    ];

    useEffect(() => {
        if (text === '' && !showDraftAReply && !showSuggestionSummaryMenu) {
            setShowDraftAReply(true);
        }
        if (showSuggestionActionsMenu) {
            setShowDraftAReply(false);
        }
    }, [text, showDraftAReply, showSuggestionSummaryMenu, showSuggestionActionsMenu]);

    const handleDraftAReply = () => {
        setShowDraftAReply(false);
        setShowSuggestionSummaryMenu(true);
    };

    const handleSummaryClick = (summary: string) => {
        setSelectedSummary(summary);
        setShowSuggestionSummaryMenu(false);
        setShowSuggestionActionsMenu(true);
    };

    const onSelectedSuggestion = (suggestion: string) => {
        handleSelectedSuggestion(suggestion);
    };

    const onAccept = () => {
        setShowSuggestionActionsMenu(false);
    };

    const onDiscard = () => {
        setShowSuggestionActionsMenu(false);
        setShowDraftAReply(true);
        handleSelectedSuggestion('');
    };

    return (
        <ContainerStyled>
            <Stack vertical>
                {showDraftAReply && <DraftAReply handleDraftAReply={handleDraftAReply} />}
                {showSuggestionSummaryMenu && (
                    <SuggestionSummaryMenu messageHistory={messageHistory} handleClick={handleSummaryClick} />
                )}
                {showSuggestionActionsMenu && (
                    <SuggestionActionsMenu
                        messageHistory={messageHistory}
                        selectedSummary={selectedSummary}
                        handleSelectedSuggestion={onSelectedSuggestion}
                        handleAccept={onAccept}
                        handleDiscard={onDiscard}
                    />
                )}
            </Stack>
        </ContainerStyled>
    );
};

const ContainerStyled = styled(Container)`
    width: 100%;
    border: ${getToken('borderWidth100')}px solid ${getToken('borderStaticPrimary')};
    border-radius: ${getToken('borderRadius100')}px;
    box-shadow: 0 ${getSpacingToken('50')}px ${getSpacingToken('200')}px -${getSpacingToken('50')}px ${getToken('borderStaticOnColor')};
    align-self: start;
    padding: ${getSpacingToken('100')}px;
`;
